import { useState, useEffect } from 'react';
import { LuMail, LuPhone, LuTruck } from 'react-icons/lu';
import DriverLayout from '../../components/DriverLayout/DriverLayout';
import { fetchCurrentUser, authFetch } from '../../lib/directus';
import styles from './DriverProfile.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.driftwoodagri.com';

export default function DriverProfile() {
  const [user, setUser]       = useState(null);
  const [driver, setDriver]   = useState(null);
  const [assignedTruck, setAssignedTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    async function loadProfile() {
      setLoading(true);
      setError('');
      try {
        const currentUser = await fetchCurrentUser();
        let driverRecord = null;

        if (currentUser?.id) {
          try {
            driverRecord = await authFetch(
              `${API_URL}/items/drivers?fields=*,truck.id,truck.truck_id,truck.make,truck.license_plate&filter[user][_eq]=${encodeURIComponent(currentUser.id)}&limit=1`
            )
              .then(async (res) => {
                const json = await res.json();
                if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch driver profile');
                return json.data?.[0] || null;
              });
          } catch {
            // Fall back to email-based lookup for older records or restricted roles.
          }
        }

        if (!driverRecord && currentUser?.email) {
          driverRecord = await authFetch(
            `${API_URL}/items/drivers?fields=*,truck.id,truck.truck_id,truck.make,truck.license_plate&filter[email][_eq]=${encodeURIComponent(currentUser.email)}&limit=1`
          )
            .then(async (res) => {
              const json = await res.json();
              if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch driver profile');
              return json.data?.[0] || null;
            });
        }

        let truckRecord = driverRecord?.truck || null;

        if (driverRecord?.id) {
          try {
            const truckRes = await authFetch(
              `${API_URL}/items/trucks?fields=id,truck_id,make,license_plate&filter[driver][_eq]=${encodeURIComponent(driverRecord.id)}&limit=1`
            );
            const truckJson = await truckRes.json();
            if (truckRes.ok) {
              truckRecord = truckJson.data?.[0] || truckRecord;
            }
          } catch {
            // Keep any embedded driver.truck value if reverse lookup fails.
          }
        }

        setUser(currentUser);
        setDriver(driverRecord);
        setAssignedTruck(truckRecord);
      } catch (err) {
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, []);

  const displayName = user ? [user.first_name, user.last_name].filter(Boolean).join(' ') || '—' : '—';

  return (
    <DriverLayout>
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Profile</h1>

        {loading && <p className={styles.loadingText}>Loading...</p>}
        {!loading && error && <p className={styles.loadingText}>{error}</p>}

        {!loading && !error && (
          <>
            {/* Avatar + name */}
            <div className={styles.avatarCard}>
              <div className={styles.avatarCircle}>
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className={styles.name}>{displayName}</p>
                <p className={styles.role}>Driver</p>
              </div>
            </div>

            {/* Contact info */}
            <div className={styles.card}>
              <p className={styles.cardTitle}>Contact information</p>
              <div className={styles.infoRow}>
                <span className={styles.infoIcon}><LuMail size={16} /></span>
                <span className={styles.infoLabel}>Email</span>
                <span className={styles.infoValue}>{user?.email || '—'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoIcon}><LuPhone size={16} /></span>
                <span className={styles.infoLabel}>Phone</span>
                <span className={styles.infoValue}>{driver?.phone || '—'}</span>
              </div>
            </div>

            {/* Assigned truck */}
            <div className={styles.card}>
              <p className={styles.cardTitle}>Assigned truck</p>
              {assignedTruck ? (
                <>
                  <div className={styles.infoRow}>
                    <span className={styles.infoIcon}><LuTruck size={16} /></span>
                    <span className={styles.infoLabel}>Truck ID</span>
                    <span className={styles.infoValue}>{assignedTruck.truck_id || '—'}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoIcon}><LuTruck size={16} /></span>
                    <span className={styles.infoLabel}>Make</span>
                    <span className={styles.infoValue}>{assignedTruck.make || '—'}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoIcon}><LuTruck size={16} /></span>
                    <span className={styles.infoLabel}>Plate</span>
                    <span className={styles.infoValue}>{assignedTruck.license_plate || '—'}</span>
                  </div>
                </>
              ) : (
                <p className={styles.noTruck}>No truck assigned yet.</p>
              )}
            </div>
          </>
        )}
      </div>
    </DriverLayout>
  );
}
